import React, { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useAuth } from "../auth/AuthContext";
import { api, toCents } from "../lib/api";
import { getStripe } from "../lib/stripe";
import { ensureStripeCustomer } from "../lib/stripeCustomer";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const PaymentForm = ({ onDone, onCancel, milestone, project, paymentIntentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user, profile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message || "Payment failed");
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      try {
        await api.post("/confirm-payment", {
          paymentIntentId: paymentIntent.id || paymentIntentId,
          userId: user.uid,
          total: milestone.amount,
          cart: [
            {
              projectId: project.id,
              milestoneId: milestone.id,
              label: `${project.title} · ${milestone.label}`,
              amount: milestone.amount,
            },
          ],
          userName: profile?.displayName || user.displayName || "",
          userEmail: user.email,
        });
      } catch (err) {
        // Payment went through on Stripe side; surface but still treat as success.
        console.error("confirm-payment bookkeeping failed", err);
      }
      onDone(paymentIntent);
      return;
    }

    setError(`Unexpected status: ${paymentIntent?.status}`);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PaymentElement />
      {error && (
        <div style={{ color: "#c0392b", fontSize: 13, fontFamily: "var(--fw-mono)" }}>{error}</div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="fw-btn fw-btn--primary" disabled={!stripe || submitting}>
          {submitting ? "Processing…" : `Pay ${fmt(milestone.amount)}`}
        </button>
      </div>
    </form>
  );
};

const PayMilestoneModal = ({ milestone, project, onClose, onPaid }) => {
  const { user, profile } = useAuth();
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        await ensureStripeCustomer({ user, profile });
        const { data } = await api.post("/create-in-house-payment-intent", {
          total: toCents(milestone.amount),
          userId: user.uid,
          userName: profile?.displayName || user.displayName || "",
          userEmail: user.email,
          cart: [
            {
              projectId: project.id,
              milestoneId: milestone.id,
              label: `${project.title} · ${milestone.label}`,
              amount: toCents(milestone.amount),
            },
          ],
        });
        if (!live) return;
        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId);
      } catch (err) {
        if (live) setError(err?.response?.data?.error || err.message || "Failed to start payment");
      }
    })();
    return () => { live = false; };
  }, [user, profile, milestone, project]);

  const stripePromise = getStripe();

  return (
    <div className="fw-modal__backdrop" onClick={onClose}>
      <div className="fw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fw-modal__head">
          <div>
            <div className="fw-modal__eyebrow">{project.title}</div>
            <h3 className="fw-modal__title">{milestone.label}</h3>
          </div>
          <div className="fw-modal__amount">{fmt(milestone.amount)}</div>
        </div>

        {error && (
          <div className="fw-empty" style={{ color: "#c0392b" }}>{error}</div>
        )}

        {!error && !clientSecret && (
          <div className="fw-empty">Preparing secure payment…</div>
        )}

        {clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
            <PaymentForm
              milestone={milestone}
              project={project}
              paymentIntentId={paymentIntentId}
              onCancel={onClose}
              onDone={(pi) => { onPaid?.(pi); onClose(); }}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default PayMilestoneModal;
